import { fail } from "src/utils";
import {
  ReviewProjectImpact_CheckoutConfigFragment,
  ReviewProjectImpact_CostCodeFragment,
  ReviewProjectImpact_ProjectCostCodeFragment,
} from "./3.ReviewImpactStep.generated";

/** A common shape for the new budget items, which come from either a change event or the computed proto items. */
export type ReviewImpactNewBudgetRow = {
  costCode: ReviewProjectImpact_CostCodeFragment;
  totalCostInCents: number;
  /** Undefined when the branch has no price data, so Markup/Price render as a dash. */
  totalPriceInCents: number | undefined;
};

export type ProjectBudgetRow = {
  costCode: ReviewProjectImpact_CostCodeFragment;
  currentBudgetInCents: number;
  newBudgetInCents: number;
  newPriceInCents: number | undefined;
};

/**
 * Given the current project cost codes and the new budget items, create the rows for the budget detail table.
 *
 * Every row is a change to today's budget, so a replacing configuration, whose items are the whole new budget, has
 * to be diffed against what the project has today first.
 */
export function createProjectBudgetRows(
  projectCostCodes: ReviewProjectImpact_ProjectCostCodeFragment[],
  newBudgetRows: ReviewImpactNewBudgetRow[],
  isReplacingConfig: boolean,
): ProjectBudgetRow[] {
  const pccByCostCodeId = projectCostCodes.keyBy((pcc) => pcc.costCode.id);
  // Prices are all-or-nothing per branch, so one priced row means the whole table can show Markup/Price
  const hasPriceData = newBudgetRows.some((row) => row.totalPriceInCents !== undefined);
  const newBudgetRowsByCostCodeId = newBudgetRows.groupBy(({ costCode }) => costCode.id);

  return [...projectCostCodes.map((pcc) => pcc.costCode), ...newBudgetRows.map(({ costCode }) => costCode)]
    .uniqueByKey("id")
    .map((costCode) => {
      const rows = newBudgetRowsByCostCodeId[costCode.id] ?? [];
      const currentBudgetInCents = pccByCostCodeId[costCode.id]?.totalCostInCents ?? 0;
      const newCostInCents = rows.sum((row) => row.totalCostInCents);
      const newPriceInCents = rows.sum((row) => row.totalPriceInCents ?? 0);
      return {
        costCode,
        currentBudgetInCents,
        newBudgetInCents: isReplacingConfig ? newCostInCents - currentBudgetInCents : newCostInCents,
        newPriceInCents: hasPriceData ? newPriceInCents : undefined,
      };
    });
}

/**
 * The project's budget before and after the change. Each row is already a delta, so the new budget is today's
 * total plus every row's change.
 */
export function reviewImpactBudgetTotals(
  projectCostCodes: ReviewProjectImpact_ProjectCostCodeFragment[],
  rows: ProjectBudgetRow[],
): { currentBudgetInCents: number; newBudgetInCents: number } {
  const currentBudgetInCents = projectCostCodes.sum((pcc) => pcc.totalCostInCents);
  return { currentBudgetInCents, newBudgetInCents: currentBudgetInCents + rows.sum((row) => row.newBudgetInCents) };
}

/** The product offering and selected options for the half of the checkout config this structure is on. */
export function resolveCheckoutConfigFields(
  ccfg: ReviewProjectImpact_CheckoutConfigFragment,
  isAdu: boolean,
): { readyPlanId: string; readyPlanOptionIds: string[] } {
  const { productOffering, aduProductOffering, mhOptionGroups, aduOptionGroups } = ccfg;
  const pof = isAdu ? aduProductOffering : productOffering;
  const optionGroups = isAdu ? aduOptionGroups : mhOptionGroups;
  return {
    readyPlanId: pof?.id ?? fail("No product offering on the checkout config"),
    readyPlanOptionIds: optionGroups.map((rpog) => rpog.selectedOption?.option.id).compact(),
  };
}
